import { isAuthenticated, pb, pocketbaseUrl } from "../lib/pocketbase";

const SYSTEM_FIELDS = new Set([
  "collectionId", "collectionName", "expand", "created", "updated", "data", "_ngCollection",
]);

const ROUTES = {
  // Le schéma historique de `core_staff_profiles` exige email/prenom et des
  // rôles obsolètes. Les fiches actuelles sont donc conservées sans perte dans
  // le stockage JSON partagé, sans modifier le schéma PocketBase existant.
  "/api/operators": { collection: "app_config", listKey: "operators" },
  "/api/team-handovers": { collection: "team_passations", listKey: "messages", embeddedKey: "reads" },
  "/api/recurring-missions": { collection: "team_missions", listKey: "missions", embeddedKey: "completions" },
  "/api/daily-tasks": { collection: "team_todos", listKey: "tasks", embeddedKey: "notes" },
  "/api/operator-notes": { collection: "personal_notes", listKey: "notes" },
  "/api/patient-followups": { collection: "patients_suivis", directList: true },
  "/api/missing-products": { collection: "rayon_demandes", listKey: "items" },
  "/api/adrenaline": { collection: "app_config", listKey: "items" },
  "/api/cpam-overpayments": { collection: "app_config", listKey: "items" },
  "/api/associated-sales": { collection: "app_config", listKey: "items" },
  "/api/product-orders": { collection: "stock_commandes", listKey: "items" },
  // Le schéma historique de `stock_commandes` impose des champs étrangers au
  // collage d'étiquettes. Le payload complet est conservé dans `app_config`.
  "/api/stock-labels": { collection: "app_config", listKey: "items" },
  "/api/shopping-list": { collection: "stock_commandes", listKey: "items" },
  "/api/generic-shortages": { collection: "stock_commandes", listKey: "items" },
  "/api/clearance-products": { collection: "stock_commandes", listKey: "items" },
  "/api/laboratories": { collection: "lab_suivis", listKey: "items" },
  "/api/laboratory-followups": { collection: "lab_suivis", listKey: "items" },
  // La collection historique `perfadom_dossiers` impose des listes fermées
  // (`etape`, `statut`) incompatibles avec le formulaire NG. Comme pour les
  // autres modules historiques incompatibles, le dossier complet est conservé
  // sans perte dans le stockage JSON partagé, sans modifier le schéma.
  "/api/perfadom": { collection: "app_config", listKey: "items" },
  "/api/mad-rentals": { collection: "app_config", listKey: "items" },
  "/api/merchandising-analyses": { collection: "app_config", listKey: "items" },
  "/api/bri-store": { collection: "app_config", listKey: "items" },
  "/api/challenges": { collection: "app_config", listKey: "items" },
  "/api/team-training": { collection: "app_config", listKey: "items" },
  "/api/weekly-advice": { collection: "app_config", listKey: "items" },
  "/api/promotions": { collection: "app_config", listKey: "items" },
  "/api/counter-alerts": { collection: "notifications", listKey: "items" },
  "/api/product-catalog": { collection: "app_config", listKey: "items" },
  "/api/nifty-pricing": { collection: "app_config", listKey: "items" },
  "/api/nifty-history": { collection: "app_config", listKey: "items" },
  "/api/windows-companion": { collection: "app_config", listKey: "items" },
};

const FUNCTIONAL_ROUTES = new Set([
  "/api/daily-brief", "/api/drive", "/api/pricing-assistant", "/api/prescription-ai",
  "/api/pharmascan-runtime", "/api/admin-backup",
  "/api/advice-sheet-ai", "/api/merchandising-ai", "/api/bri-ai",
  "/api/clearance-trigger-suggestions",
]);

const DRIVE_ROUTE = "/api/drive";
const COMPANION_API_ORIGIN = "https://static.14.84.224.46.clients.your-server.de/companion";
const CATALOG_PHARMACIES = ["Square", "Ragadan", "Laurentine"];
const COMPANION_WORKER_ORIGIN = String(
  import.meta.env.VITE_COMPANION_WORKER_URL || "https://pocketbase-ng.vincent-brasseur2.workers.dev",
).replace(/\/+$/, "");
const BACKUP_FORMAT = "portail-pharma-ng-backup";
const defaultDriveCategories = [
  "Organisation & équipes", "Administratif", "Comptoir & protocoles", "Vaccination", "MAD & formulaires",
];

let nativeFetch;
const POCKETBASE_PAGE_SIZE = 100;
const COLLECTION_CACHE_TTL_MS = 2 * 60_000;
const APP_CONFIG_CACHE_TTL_MS = 5 * 60_000;
const API_RESPONSE_CACHE_TTL_MS = 60_000;
const pendingCollectionReads = new Map();
const collectionReadCache = new Map();
const pendingGetRequests = new Map();
const successfulGetResponses = new Map();
const terminalGetResponses = new Map();

const LEGACY_PAYLOAD_PREFIX = "__portail_pharma_ng_json__:";
const VERSIONED_TEAM_ROUTES = new Set([
  "/api/team-handovers",
  "/api/recurring-missions",
]);
const VERSIONED_SHARED_ROUTES = new Set([
  "/api/operator-notes",
  "/api/shopping-list",
  "/api/product-orders",
  "/api/stock-labels",
  "/api/missing-products",
  "/api/generic-shortages",
  "/api/clearance-products",
  "/api/laboratory-followups",
  "/api/laboratories",
  "/api/nifty-history",
]);
const APP_CONFIG_FALLBACK_ROUTES = new Set([
  ...VERSIONED_TEAM_ROUTES,
  ...VERSIONED_SHARED_ROUTES,
  "/api/daily-tasks",
]);
const VERSIONED_WRITE_ROUTES = new Set([
  ...VERSIONED_TEAM_ROUTES,
  ...VERSIONED_SHARED_ROUTES,
]);
const TEAM_ROUTES = new Set([
  ...VERSIONED_TEAM_ROUTES,
  "/api/daily-tasks",
]);

function now() {
  return Date.now();
}

function usesCompanionWorker(pathname, method, searchParams) {
  if (pathname === "/api/windows-companion") return true;
  if (pathname === "/api/pharmascan-runtime") return method === "GET";
  return pathname === "/api/counter-alerts"
    && method === "GET"
    && searchParams.get("mode") === "active";
}

async function forwardCompanionWorker(pathname, searchParams, options, method) {
  const headers = new Headers(options?.headers || {});
  if (pb.authStore.token) headers.set("Authorization", pb.authStore.token);
  const controller = new AbortController();
  const relayAbort = () => controller.abort();
  options?.signal?.addEventListener("abort", relayAbort, { once: true });
  const timeout = window.setTimeout(() => controller.abort(), 12_000);
  try {
    const query = searchParams.toString();
    const target = `${COMPANION_WORKER_ORIGIN}${pathname}${query ? `?${query}` : ""}`;
    const response = await nativeFetch(target, {
      ...options,
      method,
      headers,
      signal: controller.signal,
      cache: "no-store",
    });
    if (response.status === 401 || response.status === 403) {
      window.dispatchEvent(new CustomEvent("ng-auth-required"));
    }
    return response;
  } catch (error) {
    return json({
      error: error?.name === "AbortError"
        ? "Le service Companion met trop de temps à répondre. Réessayez."
        : "Le service Companion est momentanément indisponible.",
    }, 503);
  } finally {
    window.clearTimeout(timeout);
    options?.signal?.removeEventListener("abort", relayAbort);
  }
}

async function forwardCompanionApi(pathname, searchParams, options, method) {
  const headers = new Headers(options?.headers || {});
  if (pb.authStore.token) headers.set("Authorization", pb.authStore.token);
  const controller = new AbortController();
  const relayAbort = () => controller.abort();
  options?.signal?.addEventListener("abort", relayAbort, { once: true });
  const timeout = window.setTimeout(() => controller.abort(), 15_000);
  try {
    const query = searchParams.toString();
    const target = `${COMPANION_API_ORIGIN}${pathname}${query ? `?${query}` : ""}`;
    const response = await nativeFetch(target, {
      ...options,
      method,
      headers,
      signal: controller.signal,
      cache: "no-store",
    });
    if (response.status === 401 || response.status === 403) {
      window.dispatchEvent(new CustomEvent("ng-auth-required"));
    }
    return response;
  } catch (error) {
    return json({
      error: error?.name === "AbortError"
        ? "Le service Nifty met trop de temps à répondre. Réessayez."
        : "Le service Nifty est momentanément indisponible.",
    }, 503);
  } finally {
    window.clearTimeout(timeout);
    options?.signal?.removeEventListener("abort", relayAbort);
  }
}

function numericId(value) {
  const text = String(value || "");
  let hash = 2166136261;
  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return Math.abs(hash >>> 0) || now();
}

function decodeStoredObject(value) {
  if (value && typeof value === "object" && !Array.isArray(value)) return value;
  if (typeof value !== "string" || !value.trim()) return {};
  try {
    const source = value.startsWith(LEGACY_PAYLOAD_PREFIX)
      ? value.slice(LEGACY_PAYLOAD_PREFIX.length)
      : value;
    const parsed = JSON.parse(source);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

function pharmacyLabel(value) {
  const code = String(value || "").toLocaleLowerCase("fr");
  return {
    square: "Square",
    ragadan: "Ragadan",
    laurentine: "Laurentine",
  }[code] || String(value || "");
}

function recordPayload(record) {
  const direct = Object.fromEntries(
    Object.entries(record).filter(([key]) => !SYSTEM_FIELDS.has(key)),
  );
  const configured = decodeStoredObject(record.valeur);
  const stored = decodeStoredObject(record.data);
  const embedded = [direct.detail, direct.message, direct.description]
    .map(decodeStoredObject)
    .find((value) => Object.keys(value).length) || {};
  const merged = { ...direct, ...configured, ...embedded, ...stored };
  const companionStockLabel = String(merged.comment || "").match(/poste PPSTOCK\|(stock|label)\|([A-Za-z,+]+)\|([^\s]*)/);
  const companionPharmacies = companionStockLabel
    ? companionStockLabel[2].split(/[,+]/).map(pharmacyLabel).filter((value) => ["Square", "Ragadan", "Laurentine"].includes(value))
    : [];
  let companionComment = "";
  if (companionStockLabel?.[3]) {
    try { companionComment = decodeURIComponent(companionStockLabel[3]); } catch { companionComment = companionStockLabel[3]; }
  }
  const legacyId = Number(merged.legacy_id ?? merged.legacyId ?? merged.id);
  const statuses = {
    a_faire: "open",
    a_traiter: "open",
    termine: "completed",
    terminee: "completed",
    active: "active",
    actif: "active",
    publiee: "active",
    ouverte: "active",
    en_cours: "active",
    archivee: "archived",
  };
  const priorities = { basse: "normal", normale: "normal", haute: "important", importante: "important", urgente: "urgent", information: "information" };
  const categories = { todo: "task", tache: "task", livraison: "delivery" };
  const frequencies = { biquotidienne: "twice_daily", quotidienne: "daily", hebdomadaire: "weekly", mensuelle: "monthly" };
  const pharmacy = merged.pharmacy || pharmacyLabel(merged.pharmacie_code);
  return {
    ...merged,
    title: merged.title || merged.titre || "",
    description: embedded.description || (String(merged.description || "").startsWith(LEGACY_PAYLOAD_PREFIX) ? "" : merged.description) || "",
    details: merged.details || embedded.detail || "",
    message: embedded.message || (String(merged.message || "").startsWith(LEGACY_PAYLOAD_PREFIX) ? "" : merged.message) || "",
    status: companionStockLabel ? (merged.status === "ordered" ? "completed" : merged.status === "new" ? "open" : merged.status || "open") : merged.status || statuses[merged.statut] || merged.statut || "",
    priority: merged.priority || priorities[merged.priorite] || merged.priorite || "normal",
    kind: merged.kind || categories[merged.categorie] || merged.categorie || "",
    frequency: merged.frequency || frequencies[merged.frequence] || merged.frequence || "",
    pharmacy,
    pharmacies: companionStockLabel ? companionPharmacies : Array.isArray(merged.pharmacies) ? merged.pharmacies : pharmacy ? [pharmacy] : [],
    requestType: companionStockLabel?.[1] || merged.requestType || merged.request_type,
    comment: companionStockLabel ? companionComment : merged.comment,
    createdBy: companionStockLabel ? (merged.requestedBy || "Compagnon achat") : merged.createdBy || merged.created_by,
    _companionStockLabel: Boolean(companionStockLabel),
    id: Number.isFinite(legacyId) && legacyId > 0 ? legacyId : numericId(record.id),
    _pbId: record.id,
    created_at: Number(merged.created_at) || Date.parse(record.created || "") || 0,
    updated_at: Number(merged.updated_at) || Date.parse(record.updated || "") || 0,
  };
}

function routeMatches(item, pathname, collection) {
  const marker = item.record_type ?? item.route ?? item._route ?? item.source_module;
  if (item._companionStockLabel && pathname === "/api/stock-labels") return true;
  if (item._companionStockLabel && pathname === "/api/product-orders") return false;
  if (marker) return marker === pathname;
  const routesForCollection = Object.entries(ROUTES)
    .filter(([, value]) => value.collection === collection);
  return routesForCollection.length === 1;
}

function httpStatus(error) {
  return Number(error?.status || error?.response?.status || 0);
}

function isClientError(error) {
  const status = httpStatus(error);
  return status >= 400 && status < 500;
}

function clearReadCaches() {
  collectionReadCache.clear();
  successfulGetResponses.clear();
  terminalGetResponses.clear();
}

async function listCollectionRecords(collection, filter = "", maximum = Infinity) {
  const normalizedMaximum = Number.isFinite(maximum) ? Math.max(1, Math.floor(maximum)) : Infinity;
  const key = `${collection}\n${filter}\n${normalizedMaximum}`;
  const cached = collectionReadCache.get(key);
  if (cached && cached.expiresAt > now()) return cached.records;
  if (cached) collectionReadCache.delete(key);
  const pending = pendingCollectionReads.get(key);
  if (pending) return pending;
  const request = (async () => {
    const records = [];
    const pageSize = Math.min(POCKETBASE_PAGE_SIZE, normalizedMaximum);
    for (let page = 1; ; page += 1) {
      const options = { requestKey: null, skipTotal: true };
      if (filter) options.filter = filter;
      const result = await pb.collection(collection).getList(page, pageSize, options);
      records.push(...result.items);
      if (records.length >= normalizedMaximum || result.items.length < pageSize) break;
    }
    const result = records.slice(0, normalizedMaximum);
    collectionReadCache.set(key, {
      expiresAt: now() + (collection === "app_config" ? APP_CONFIG_CACHE_TTL_MS : COLLECTION_CACHE_TTL_MS),
      records: result,
    });
    return result;
  })();
  pendingCollectionReads.set(key, request);
  try {
    return await request;
  } finally {
    if (pendingCollectionReads.get(key) === request) pendingCollectionReads.delete(key);
  }
}

async function listRoute(pathname, spec, maximum = Infinity) {
  const read = async (collection) => {
    const filter = collection === "app_config" ? `description = "Donnée du module ${pathname}"` : "";
    return (await listCollectionRecords(collection, filter, maximum))
      .map((record) => ({ item: recordPayload(record), collection }));
  };
  let primary = [];
  try {
    primary = await read(spec.collection);
  } catch (error) {
    if (isClientError(error) || !APP_CONFIG_FALLBACK_ROUTES.has(pathname)) throw error;
  }
  const fallback = APP_CONFIG_FALLBACK_ROUTES.has(pathname) && spec.collection !== "app_config" ? await read("app_config") : [];
  const sorted = [...primary, ...fallback]
    .filter(({ item, collection }) => routeMatches(item, pathname, collection))
    .map(({ item }) => item)
    .sort((left, right) => Number(right.updated_at || 0) - Number(left.updated_at || 0));
  const latestById = new Map();
  for (const item of sorted) {
    if (!latestById.has(String(item.id))) latestById.set(String(item.id), item);
  }
  return [...latestById.values()].filter((item) => item.deleted !== true && item.status !== "deleted");
}

async function listGenericShortagesDirect() {
  const records = [];
  for (let page = 1; ; page += 1) {
    const endpoint = new URL(`${pocketbaseUrl}/api/collections/app_config/records`);
    endpoint.searchParams.set("page", String(page));
    endpoint.searchParams.set("perPage", String(POCKETBASE_PAGE_SIZE));
    endpoint.searchParams.set("skipTotal", "1");
    endpoint.searchParams.set("filter", 'description = "Donnée du module /api/generic-shortages"');
    const response = await nativeFetch(endpoint, { cache: "no-store" });
    if (!response.ok) {
      const error = new Error(`Lecture des génériques impossible (${response.status}).`);
      error.status = response.status;
      throw error;
    }
    const payload = await response.json();
    const pageItems = Array.isArray(payload.items) ? payload.items : [];
    records.push(...pageItems);
    if (pageItems.length < POCKETBASE_PAGE_SIZE) break;
  }
  const sorted = records
    .map(recordPayload)
    .filter((item) => routeMatches(item, "/api/generic-shortages", "app_config"))
    .sort((left, right) => Number(right.updated_at || 0) - Number(left.updated_at || 0));
  const latestById = new Map();
  for (const item of sorted) {
    if (!latestById.has(String(item.id))) latestById.set(String(item.id), item);
  }
  return [...latestById.values()].filter((item) => item.deleted !== true && item.status !== "deleted");
}

async function resolveRecord(pathname, spec, legacyId) {
  const collections = APP_CONFIG_FALLBACK_ROUTES.has(pathname) && spec.collection !== "app_config"
    ? [spec.collection, "app_config"]
    : [spec.collection];
  const matches = [];
  for (const collection of collections) {
    const filter = collection === "app_config" ? `description = "Donnée du module ${pathname}"` : "";
    let records = [];
    try {
      records = await listCollectionRecords(collection, filter);
    } catch (error) {
      if (isClientError(error) || !APP_CONFIG_FALLBACK_ROUTES.has(pathname) || collection === "app_config") throw error;
      continue;
    }
    const match = records
      .filter((record) => {
        const item = recordPayload(record);
        return routeMatches(item, pathname, collection) && String(item.id) === String(legacyId);
      })
      .sort((left, right) => Number(recordPayload(right).updated_at || 0) - Number(recordPayload(left).updated_at || 0))[0];
    if (match) matches.push({ ...match, _ngCollection: collection });
  }
  return matches
    .sort((left, right) => Number(recordPayload(right).updated_at || 0) - Number(recordPayload(left).updated_at || 0))[0]
    || null;
}

function storedPayload(pathname, payload, defaults = {}) {
  const timestamp = now();
  const legacyId = Number(payload.legacy_id ?? payload.legacyId ?? payload.id) || timestamp;
  return {
    ...defaults,
    ...payload,
    id: legacyId,
    legacy_id: legacyId,
    record_type: pathname,
    created_at: Number(payload.created_at) || timestamp,
    updated_at: timestamp,
  };
}

function pharmacyCode(payload) {
  const value = payload.pharmacie_code
    || payload.pharmacyCode
    || payload.pharmacy
    || (Array.isArray(payload.pharmacies) ? payload.pharmacies[0] : "")
    || "reseau";
  return String(value).normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLocaleLowerCase("fr").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

function appConfigBody(payload, versioned = false) {
  const route = payload.record_type || "portail-ng";
  const id = payload.legacy_id || payload.id || now();
  const version = versioned ? `:${payload.updated_at || now()}` : "";
  return {
    cle: `portail-ng:${route}:${id}${version}`,
    valeur: payload,
    description: `Donnée du module ${route}`,
    data: payload,
  };
}

function pocketBaseBody(payload) {
  const route = payload.record_type || "portail-ng";
  if (ROUTES[route]?.collection === "app_config") return appConfigBody(payload);
  if (TEAM_ROUTES.has(route)) {
    const title = String(payload.titre || payload.title || payload.name || "Sans titre").trim();
    const priorities = { normal: "normale", important: "haute", urgent: "urgente", information: "basse" };
    const statuses = { open: "a_faire", completed: "termine", active: "a_faire", archived: "archive" };
    const frequencies = { twice_daily: "biquotidienne", daily: "quotidienne", weekly: "hebdomadaire", monthly: "mensuelle" };
    const categories = { task: "todo", delivery: "livraison" };
    const incomingStatus = payload.statut || payload.status || "";
    const defaultStatus = route === "/api/team-handovers" ? "a_traiter" : "a_faire";
    const storedStatus = incomingStatus === "active" ? defaultStatus : statuses[incomingStatus] || incomingStatus || defaultStatus;
    const common = {
      titre: title,
      pharmacie_code: pharmacyCode(payload),
      priorite: payload.priorite || priorities[payload.priority] || payload.priority || "normale",
      statut: storedStatus,
      // `data` conserve le payload NG complet. Les colonnes historiques restent
      // des champs métier courts afin de respecter leurs limites PocketBase
      // (notamment `team_todos.detail`, limité à 600 caractères).
      detail: String(payload.details || payload.detail || "").slice(0, 600),
      source_module: route,
      source_ref: String(payload.source_ref || payload.legacy_id || payload.id || ""),
    };
    if (route === "/api/daily-tasks") {
      return {
        ...common,
        statut: storedStatus,
        categorie: payload.categorie || categories[payload.kind] || payload.kind || "todo",
        data: payload,
      };
    }
    if (route === "/api/recurring-missions") {
      return {
        ...common,
        frequence: payload.frequence || frequencies[payload.frequency] || payload.frequency || "quotidienne",
        description: String(payload.description || payload.details || ""),
        active: payload.active !== false,
        data: payload,
      };
    }
    return {
      ...common,
      message: String(payload.message || payload.details || ""),
      active: payload.status !== "archived",
      data: payload,
    };
  }
  // `data` est la source canonique NG. Les champs de premier niveau permettent
  // aussi de fonctionner avec des collections déjà typées plus finement.
  return { ...payload, data: payload };
}

async function savePocketBaseRecord(collection, method, recordId, body) {
  // L'identifiant numerique historique reste dans `data` pour les ecrans,
  // mais ne doit jamais etre envoye comme identifiant natif PocketBase :
  // PocketBase attend son propre identifiant de 15 caracteres a la creation.
  const persistedBody = { ...body };
  delete persistedBody.id;
  const saved = method === "create"
    ? await pb.collection(collection).create(persistedBody, { requestKey: null })
    : await pb.collection(collection).update(recordId, persistedBody, { requestKey: null });
  clearReadCaches();
  return saved;
}

function recordStoredPayload(record, payload) {
  const item = recordPayload(record);
  return String(item.record_type || "") === String(payload.record_type || "")
    && String(item.legacy_id || "") === String(payload.legacy_id || "");
}

async function createPersistedRecord(pathname, spec, payload) {
  if (VERSIONED_SHARED_ROUTES.has(pathname)) {
    const fallback = await savePocketBaseRecord("app_config", "create", null, appConfigBody(payload, true));
    return { ...fallback, _ngCollection: "app_config" };
  }
  if (!TEAM_ROUTES.has(pathname)) {
    return savePocketBaseRecord(spec.collection, "create", null, pocketBaseBody(payload));
  }
  try {
    const primary = await savePocketBaseRecord(spec.collection, "create", null, pocketBaseBody(payload));
    if (recordStoredPayload(primary, payload)) return { ...primary, _ngCollection: spec.collection };
    try {
      await pb.collection(spec.collection).delete(primary.id, { requestKey: null });
    } catch {
      // Un enregistrement incomplet ne doit pas bloquer le repli JSON.
    }
  } catch (error) {
    if (isClientError(error)) throw error;
    // Certains schémas historiques sont plus stricts que les écrans actuels.
  }
  const fallback = await savePocketBaseRecord("app_config", "create", null, appConfigBody(payload, true));
  return { ...fallback, _ngCollection: "app_config" };
}

function targetCollection(record, spec) {
  return record?._ngCollection || spec.collection;
}

function bodyForCollection(collection, payload) {
  return collection === "app_config" ? appConfigBody(payload) : pocketBaseBody(payload);
}

function json(data, status = 200) {
  return new Response(JSON.stringify(data), {
    status,
    headers: { "Content-Type": "application/json; charset=utf-8", "Cache-Control": "no-store" },
  });
}

function pocketBaseErrorDetail(error) {
  const fields = error?.response?.data;
  if (fields && typeof fields === "object" && !Array.isArray(fields)) {
    const details = Object.entries(fields).map(([field, issue]) => {
      const message = issue && typeof issue === "object"
        ? issue.message || issue.code || JSON.stringify(issue)
        : String(issue);
      return `${field}: ${message}`;
    });
    if (details.length) return details.join(" ; ");
  }
  return error?.message || "Erreur PocketBase NG";
}

async function requestBody(options) {
  if (!options?.body) return {};
  if (typeof options.body === "string") return JSON.parse(options.body || "{}");
  if (options.body instanceof FormData) {
    const values = {};
    for (const [key, value] of options.body.entries()) values[key] = value;
    return values;
  }
  return {};
}

async function fileToDataUrl(file) {
  const buffer = await file.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let offset = 0; offset < bytes.length; offset += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + 0x8000));
  }
  return `data:${file.type || "application/octet-stream"};base64,${btoa(binary)}`;
}

function configuredPayload() {
  return { configured: true, model: "Assistant NG intégré" };
}

function dailyBrief() {
  const date = new Date();
  const formatter = new Intl.DateTimeFormat("fr-FR", { weekday: "long", day: "numeric", month: "long", timeZone: "Europe/Paris" });
  return {
    updatedAt: now(),
    nameDay: "",
    today: { label: formatter.format(date), temperature: null, summary: "Données météo non requises pour utiliser le portail." },
    tomorrow: null,
    sea: null,
  };
}

function pricingAnswer(body) {
  if (body.mode === "catalog_batch") {
    return {
      analyses: (Array.isArray(body.products) ? body.products : []).map((product) => ({
        ean: product.ean,
        sensitivity: "sensitive",
        reason: "Positionnement prudent conseillé ; valider avec la marge et le prix de marché.",
      })),
    };
  }
  if (body.mode === "sensitivity") {
    return {
      sensitivity: "sensitive",
      confidence: "medium",
      reason: "Le prix reste un critère visible pour ce produit.",
      signals: ["Comparer le prix réseau", "Préserver la marge minimale"],
    };
  }
  return {
    answer: `Prix conseillé : ${Number(body.simulatedPrice || body.localRecommendation?.target || 0).toFixed(2)} € TTC. Vérifiez la marge, le prix réseau et la cohérence avec le rayon avant validation.`,
  };
}

function prescriptionAnswer(body) {
  const context = String(body.context || "").trim();
  const firstAnswer = context.match(/Réponse\s*:\s*([^\n]+)/i)?.[1];
  const firstCondition = context.match(/Condition\s*:\s*([^\n]+)/i)?.[1];
  return {
    answer: [firstAnswer || "La réponse doit être vérifiée dans la règle métier affichée.", firstCondition ? `Condition : ${firstCondition}` : "Contrôlez le prescripteur, le produit et les limites réglementaires avant délivrance."].join("\n\n"),
  };
}

function adviceSheetAnswer(body) {
  const products = (Array.isArray(body.products) ? body.products : []).map((product) => ({
    ...product,
    role: product.role || "Produit conseillé",
    dosage: product.dosage || "Selon la notice et le profil du patient",
    instructions: product.instructions || "Expliquer l’utilisation, les précautions et les signes devant conduire à consulter.",
  }));
  return {
    objective: body.objective || "Soulager les symptômes et sécuriser l’utilisation des produits conseillés.",
    lifestyle: body.lifestyle || "Hydratation, repos et surveillance de l’évolution.",
    transmission: body.transmission || "Respecter les mesures d’hygiène adaptées à la situation.",
    prevention: body.prevention || "Lire les notices et éviter les associations non validées.",
    takeaway: body.takeaway || "Demander un avis médical en cas d’aggravation ou de symptôme inhabituel.",
    products,
  };
}

function merchandisingAnswer(body) {
  return {
    id: now(), pharmacy: body.pharmacy || "", department: body.department || "",
    score: 72,
    summary: "Rayon globalement lisible. Quelques ajustements simples peuvent améliorer l’impact visuel.",
    criteria: [
      { name: "Implantation", score: 72, finding: "Organisation générale exploitable, à vérifier sur toute la hauteur du meuble." },
      { name: "Visibilité", score: 70, finding: "Les références principales doivent rester avancées et dégagées." },
      { name: "Lisibilité", score: 74, finding: "La lecture par besoin client peut être renforcée." },
      { name: "Prix & promotions", score: 68, finding: "Contrôler l’alignement et la présence des étiquettes." },
      { name: "Assortiment apparent", score: 76, finding: "Vérifier les trous et doublons avec les données de stock." },
    ],
    recommendations: [
      { title: "Regrouper par besoin", action: "Regrouper les produits correspondant au même besoin client et aligner leurs facings.", priority: "high", x: 50, y: 45 },
      { title: "Harmoniser les prix", action: "Mettre les prix et promotions sur une ligne de lecture commune.", priority: "medium", x: 50, y: 75 },
      { title: "Corriger les ruptures visuelles", action: "Avancer les produits et signaler les emplacements vides à contrôler.", priority: "medium", x: 70, y: 55 },
    ],
    createdAt: now(),
  };
}

function briAnswer(body) {
  return {
    result: {
      title: String(body.title || "Analyse BRI"),
      summary: "Analyse préparée à partir des informations saisies.",
      recommendations: ["Vérifier les données", "Prioriser les écarts les plus importants", "Tracer la décision prise"],
    },
    model: "Assistant NG intégré",
  };
}

async function driveItems() {
  const spec = { collection: "app_config" };
  return listRoute(DRIVE_ROUTE, spec);
}

function driveListResponse(items) {
  const storedCategories = items.filter((item) => item.kind === "category");
  const categories = storedCategories.length
    ? storedCategories.map((item) => ({ id: item.id, name: item.name, sort_order: Number(item.sortOrder || item.sort_order || 0) }))
    : defaultDriveCategories.map((name, index) => ({ id: index + 1, name, sort_order: index }));
  const documents = items.filter((item) => item.kind === "document").map((item) => ({
    ...item,
    original_name: item.originalName || item.original_name || item.title,
    category_id: Number(item.categoryId || item.category_id || 1),
    category_name: categories.find((category) => category.id === Number(item.categoryId || item.category_id || 1))?.name || "Documents",
    content_type: item.contentType || item.content_type || "application/octet-stream",
    size_bytes: Number(item.sizeBytes || item.size_bytes || 0),
  }));
  return { categories, documents };
}

async function handleDrive(searchParams, options) {
  const method = String(options?.method || "GET").toUpperCase();
  const spec = { collection: "app_config" };
  const items = await driveItems();
  const downloadId = Number(searchParams.get("download") || 0);
  if (method === "GET" && downloadId) {
    const item = items.find((entry) => Number(entry.id) === downloadId && entry.kind === "document");
    if (!item?.dataUrl) return json({ error: "Document introuvable." }, 404);
    const response = await nativeFetch(item.dataUrl);
    const headers = new Headers(response.headers);
    headers.set("Content-Disposition", `attachment; filename*=UTF-8''${encodeURIComponent(item.originalName || item.title || "document")}`);
    return new Response(await response.arrayBuffer(), { headers });
  }
  if (method === "GET") return json(driveListResponse(items));
  if (!isAuthenticated()) return requireAuthentication();
  if (method === "POST") {
    const body = await requestBody(options);
    const file = body.file;
    if (!(file instanceof File) || !file.size) return json({ error: "Choisissez un fichier." }, 400);
    if (file.size > 8 * 1024 * 1024) return json({ error: "Le fichier dépasse 8 Mo." }, 413);
    const categoryId = Number(body.categoryId || 1);
    const item = await createRecord(DRIVE_ROUTE, spec, {
      kind: "document",
      title: String(body.title || file.name.replace(/\.[^.]+$/, "")),
      originalName: file.name,
      categoryId,
      pharmacies: body.pharmacies || "[]",
      contentType: file.type || "application/octet-stream",
      sizeBytes: file.size,
      uploadedBy: String(body.uploadedBy || ""),
      dataUrl: await fileToDataUrl(file),
    });
    return json({ ok: true, id: item.id }, 201);
  }
  const body = await requestBody(options);
  if (method === "PATCH") {
    const record = await resolveRecord(DRIVE_ROUTE, spec, body.id);
    if (!record && body.type === "category") {
      await createRecord(DRIVE_ROUTE, spec, { id: body.id, kind: "category", name: body.name, sortOrder: body.sortOrder || 0 });
      return json({ ok: true });
    }
    if (!record) return json({ error: "Élément introuvable." }, 404);
    const current = recordPayload(record);
    const updated = storedPayload(DRIVE_ROUTE, {
      ...current,
      name: body.type === "category" ? body.name : current.name,
      sortOrder: body.type === "category" ? body.sortOrder : current.sortOrder,
      title: body.type === "document" ? body.title : current.title,
      categoryId: body.type === "document" ? body.categoryId : current.categoryId,
      id: current.id,
      created_at: current.created_at,
    });
    delete updated._pbId;
    await pb.collection(spec.collection).update(record.id, pocketBaseBody(updated), { requestKey: null });
    return json({ ok: true });
  }
  if (method === "DELETE") {
    await deleteRecord(DRIVE_ROUTE, spec, searchParams.get("id"));
    return json({ ok: true });
  }
  return json({ error: "Méthode non prise en charge." }, 405);
}

async function exportBackup() {
  const collections = [...new Set(Object.values(ROUTES).map((spec) => spec.collection))];
  const tables = [];
  for (const collection of collections) {
    const records = await listCollectionRecords(collection);
    tables.push({ name: collection, columns: ["data"], rows: records.map(recordPayload) });
  }
  const backup = { format: "portail-pharma-backup", version: 1, exportedAt: new Date().toISOString(), tables };
  const stamp = backup.exportedAt.replace(/[:.]/g, "-");
  return new Response(JSON.stringify(backup), {
    headers: {
      "Content-Type": "application/json; charset=utf-8",
      "Content-Disposition": `attachment; filename="portail-pharma-ng-${stamp}.json"`,
      "Cache-Control": "no-store",
    },
  });
}

async function restoreBackup(options) {
  if (!isAuthenticated()) return requireAuthentication();
  const backup = await requestBody(options);
  if (backup?.format !== "portail-pharma-backup" || !Array.isArray(backup.tables)) {
    return json({ error: "Ce fichier n’est pas une sauvegarde Portail Pharma valide." }, 400);
  }
  const allowed = new Set(Object.values(ROUTES).map((spec) => spec.collection));
  let restoredRows = 0;
  let restoredTables = 0;
  for (const table of backup.tables) {
    if (!allowed.has(table?.name) || !Array.isArray(table.rows)) continue;
    let tableCount = 0;
    for (const row of table.rows.slice(0, 250000)) {
      if (!row || typeof row !== "object") continue;
      const payload = { ...row };
      delete payload._pbId;
      await pb.collection(table.name).create(pocketBaseBody(payload), { requestKey: null });
      restoredRows += 1;
      tableCount += 1;
    }
    if (tableCount) restoredTables += 1;
  }
  return json({ ok: true, restoredRows, restoredTables, skippedTables: [] });
}

function requireAuthentication() {
  if (typeof window !== "undefined") window.dispatchEvent(new CustomEvent("ng-auth-required"));
  return json({ error: "Connectez l’équipe pour enregistrer cette action." }, 401);
}

async function handleFunctionalRoute(pathname, searchParams, options) {
  const method = String(options?.method || "GET").toUpperCase();
  if (pathname === "/api/drive") return handleDrive(searchParams, options);
  if (pathname === "/api/admin-backup") return method === "GET" ? exportBackup() : restoreBackup(options);
  if (pathname === "/api/daily-brief") return json(dailyBrief());
  if (pathname === "/api/pricing-assistant") return json(method === "GET" ? configuredPayload() : pricingAnswer(await requestBody(options)));
  if (pathname === "/api/prescription-ai") return json(method === "GET" ? configuredPayload() : prescriptionAnswer(await requestBody(options)));
  if (pathname === "/api/advice-sheet-ai") return json(adviceSheetAnswer(await requestBody(options)));
  if (pathname === "/api/merchandising-ai") return json(merchandisingAnswer(await requestBody(options)));
  if (pathname === "/api/bri-ai") return json(briAnswer(await requestBody(options)));
  if (pathname === "/api/pharmascan-runtime") return json({ version: "2.0.14", url: "https://portail-pharma-ng.pages.dev/downloads/pharmascan-runtime/manifest.json", configured: true });
  if (pathname === "/api/clearance-trigger-suggestions") {
    const body = await requestBody(options);
    const catalogItems = await listRoute("/api/product-catalog", ROUTES["/api/product-catalog"]);
    const words = `${body.directive || ""} ${body.product || ""}`.toLocaleLowerCase("fr").split(/\W+/).filter((word) => word.length > 3);
    const items = catalogItems.filter((item) => words.some((word) => String(item.product || "").toLocaleLowerCase("fr").includes(word))).slice(0, 18);
    return json({ items, source: "catalogue", queries: words.slice(0, 8), target: null });
  }
  return json({ error: "Fonction NG inconnue." }, 404);
}

function getDefaults(pathname, body) {
  if (pathname === "/api/daily-tasks") return { status: "open", notes: [], kind: body.kind || "task" };
  if (pathname === "/api/operator-notes") return { status: "open", completed_at: null };
  if (pathname === "/api/patient-followups") return { status: "active" };
  if (pathname === "/api/team-handovers") return { status: "active", reads: [] };
  if (pathname === "/api/recurring-missions") return { active: true, completions: [] };
  if (pathname === "/api/laboratory-followups") return { status: "planned" };
  if (pathname === "/api/stock-labels") return { status: "open", result: "" };
  return {};
}

async function createRecord(pathname, spec, body) {
  const payload = storedPayload(pathname, body, getDefaults(pathname, body));
  const created = await createPersistedRecord(pathname, spec, payload);
  const item = recordPayload(created);

  if (pathname === "/api/operator-notes" && body.copyTo) {
    const copy = storedPayload(pathname, {
      ...body,
      id: now() + 1,
      owner: body.copyTo,
      copied_from: body.owner,
      copyTo: "",
    }, getDefaults(pathname, body));
    await createPersistedRecord(pathname, spec, copy);
  }
  return item;
}

function appendEmbedded(current, key, entry, identity) {
  const items = Array.isArray(current[key]) ? current[key] : [];
  const withoutPrevious = identity
    ? items.filter((item) => !identity(item))
    : items;
  return [...withoutPrevious, entry];
}

async function patchRecord(pathname, spec, body) {
  if (
    VERSIONED_TEAM_ROUTES.has(pathname)
    && body.id
    && body.title
    && (!body.action || body.action === "update" || body.action === "edit")
  ) {
    const version = storedPayload(pathname, {
      ...getDefaults(pathname, body),
      ...body,
      id: body.id,
    });
    return recordPayload(await createPersistedRecord(pathname, spec, version));
  }

  if (pathname === "/api/recurring-missions" && body.action === "uncomplete") {
    const records = await listRoute(pathname, spec);
    const owner = records.find((item) => (item.completions || []).some((entry) => String(entry.id) === String(body.completionId)));
    if (!owner) return { ok: true };
    const record = await resolveRecord(pathname, spec, owner.id);
    const completions = (owner.completions || []).filter((entry) => String(entry.id) !== String(body.completionId));
    const updated = storedPayload(pathname, { ...owner, completions, id: owner.id, created_at: owner.created_at });
    const collection = targetCollection(record, spec);
    const saved = VERSIONED_TEAM_ROUTES.has(pathname)
      ? await createPersistedRecord(pathname, spec, updated)
      : await savePocketBaseRecord(collection, "update", record.id, bodyForCollection(collection, updated));
    return recordPayload(saved);
  }

  const record = await resolveRecord(pathname, spec, body.id);
  if (!record) throw new Error("Enregistrement NG introuvable.");
  const current = recordPayload(record);
  let updates = { ...body };

  if (pathname === "/api/daily-tasks") {
    if (body.action === "complete") updates = { status: "completed", completed_by: body.operator || "", completed_at: now() };
    if (body.action === "reopen") updates = { status: "open", completed_by: "", completed_at: null };
    if (body.action === "note") {
      const note = { id: now(), task_id: current.id, author: body.operator || "", body: body.note || "", created_at: now() };
      updates = { notes: appendEmbedded(current, "notes", note) };
    }
  } else if (pathname === "/api/operator-notes") {
    if (body.action === "complete") updates = { status: "completed", completed_at: now() };
    if (body.action === "reopen") updates = { status: "open", completed_at: null };
  } else if (pathname === "/api/team-handovers") {
    if (body.action === "read") {
      const read = { id: now(), handover_id: current.id, pharmacy: body.pharmacy, operator: body.operator, read_at: now() };
      updates = { reads: appendEmbedded(current, "reads", read, (item) => item.pharmacy === body.pharmacy && item.operator === body.operator) };
    }
    if (body.action === "archive") updates = { status: "archived", archived_at: now(), archived_by: body.operator || "" };
    if (body.action === "reopen") updates = { status: "active", archived_at: null, archived_by: "" };
  } else if (pathname === "/api/recurring-missions" && body.action === "complete") {
    const completion = { id: now(), mission_id: current.id, pharmacy: body.pharmacy, completed_by: body.operator, period_key: body.periodKey, completed_at: now() };
    updates = { completions: appendEmbedded(current, "completions", completion, (item) => item.pharmacy === body.pharmacy && item.period_key === body.periodKey) };
  } else if (pathname === "/api/patient-followups") {
    if (body.action === "close") updates = { status: "closed" };
    if (body.action === "reopen") updates = { status: "active" };
  } else if (pathname === "/api/perfadom") {
    if (body.action === "close") updates = { status: "closed" };
    if (body.action === "reopen") updates = { status: "active" };
    if (body.action === "reassign") updates = { operator: body.operator || current.operator, pharmacy: body.pharmacy || current.pharmacy };
    if (body.action === "toggle_week") updates = { invoiceStatus: { ...(current.invoiceStatus || {}), [body.weekKey]: Boolean(body.invoiced) } };
  } else if (pathname === "/api/mad-rentals") {
    if (body.action === "return") updates = { status: "returned", returnedAt: now(), returnedBy: body.returnedBy || "" };
    if (body.action === "extend") {
      const date = new Date(`${current.expectedReturnDate || current.expected_return_date || new Date().toISOString().slice(0, 10)}T12:00:00`);
      date.setDate(date.getDate() + Math.max(1, Math.min(180, Number(body.days) || 7)));
      updates = { expectedReturnDate: date.toISOString().slice(0, 10) };
    }
    if (body.action === "prescription_received") updates = { prescriptionStatus: "received", prescriptionRenewalDate: body.prescriptionRenewalDate || "" };
    if (body.action === "equipment") updates = { ...body, kind: "equipment", status: body.equipmentStatus || current.status || "available" };
  } else if (pathname === "/api/product-orders") {
    if (body.action === "link_laboratory") updates = { laboratory: body.laboratory || "", basketItemId: body.basketItemId || current.basketItemId };
    if (body.action === "order" || body.action === "ordered") updates = { status: "ordered", orderedAt: now(), orderedBy: body.operator || body.addedBy || "" };
    if (body.action === "reopen") updates = { status: current.kind === "basket" ? "waiting" : "new", orderedAt: null, orderedBy: "" };
    if (body.action === "set_status") updates = { status: body.status || current.status };
  } else if (pathname === "/api/adrenaline" && body.action === "acknowledge") {
    updates = { status: "acknowledged", acknowledgedAt: now(), acknowledgedBy: body.operator || "" };
  } else if (pathname === "/api/shopping-list") {
    if (body.action === "purchase") updates = { status: "purchased", purchasedAt: now(), purchasedBy: body.purchasedBy || body.operator || "" };
    if (body.action === "reopen") updates = { status: "open", purchasedAt: null, purchasedBy: "" };
  } else if (pathname === "/api/stock-labels") {
    if (body.action === "complete") updates = { status: "completed", result: body.result || "ok", completedAt: now(), completedBy: body.operator || "" };
    if (body.action === "reopen") updates = { status: "open", result: "", completedAt: null, completedBy: "" };
  } else if (pathname === "/api/generic-shortages") {
    if (body.action === "return") updates = { status: "returned", returnedAt: now(), returnedBy: body.operator || "" };
    if (body.action === "reactivate") updates = { status: "active", returnedAt: null, returnedBy: "" };
  } else if (pathname === "/api/missing-products") {
    if (body.action === "check") updates = { status: "active", lastCheckedAt: now(), lastCheckedBy: body.operator || body.checkedBy || "" };
    if (body.action === "obtain") updates = { status: "obtained", obtainedAt: now(), obtainedBy: body.operator || body.obtainedBy || "" };
    if (body.action === "reactivate") updates = { status: "active", checkedAt: null, obtainedAt: null };
  } else if (pathname === "/api/clearance-products") {
    if (body.action === "sold") updates = { soldCount: Number(current.soldCount || current.sold_count || 0) + 1, lastSoldAt: now(), lastSoldPharmacy: body.soldPharmacy || "" };
    if (body.action === "save") updates = { ...body };
  } else if (pathname === "/api/laboratory-followups") {
    if (body.action === "complete") updates = { status: "completed", outcome: body.outcome || "", completedAt: now(), completedBy: body.operator || "" };
    if (body.action === "reopen") updates = { status: "planned", completedAt: null, completedBy: "" };
    if (body.action === "reschedule") updates = { status: "planned", scheduledDate: body.scheduledDate || current.scheduledDate, scheduledTime: body.scheduledTime || current.scheduledTime };
    if (body.action === "preparation") updates = { preparationStatus: { ...(current.preparationStatus || {}), [body.pharmacy]: body.status || body.prepared || true } };
    if (body.action === "prepare_visit") updates = { preparationStatus: body.preparationStatus || current.preparationStatus || {} };
  } else if (body.action === "complete" || body.action === "done" || body.action === "close") {
    updates = { status: "completed", completed_at: now(), completed_by: body.operator || "" };
  } else if (body.action === "reopen") {
    updates = { status: "open", completed_at: null };
  }

  const payload = storedPayload(pathname, {
    ...current,
    ...updates,
    id: current.id,
    created_at: current.created_at,
  });
  delete payload._pbId;
  if (VERSIONED_WRITE_ROUTES.has(pathname)) {
    return recordPayload(await createPersistedRecord(pathname, spec, payload));
  }
  const collection = targetCollection(record, spec);
  const saved = await savePocketBaseRecord(collection, "update", record.id, bodyForCollection(collection, payload));
  return recordPayload(saved);
}

async function deleteRecord(pathname, spec, id) {
  if (VERSIONED_WRITE_ROUTES.has(pathname) && id) {
    const tombstone = storedPayload(pathname, {
      id: Number(id),
      deleted: true,
      status: "deleted",
    });
    // Les collections historiques d'équipe utilisent des listes fermées pour
    // `statut` et refusent donc la valeur technique `deleted`. Une suppression
    // versionnée est une trace de synchronisation, pas une mission active : elle
    // doit rester dans le stockage JSON compatible, où `listRoute` la fusionne
    // avec les versions précédentes avant de masquer l'élément.
    await savePocketBaseRecord("app_config", "create", null, appConfigBody(tombstone, true));
    return;
  }
  const record = await resolveRecord(pathname, spec, id);
  if (!record) return;
  await pb.collection(targetCollection(record, spec)).delete(record.id, { requestKey: null });
  clearReadCaches();
}

function legacyListResponse(pathname, spec, items) {
  if (spec.directList) return items;
  if (pathname === "/api/recurring-missions") {
    return { missions: items, completions: items.flatMap((item) => item.completions || []) };
  }
  if (pathname === "/api/team-handovers") {
    return { messages: items, reads: items.flatMap((item) => item.reads || []), syncedAt: now() };
  }
  if (pathname === "/api/daily-tasks") {
    return { tasks: items, notes: items.flatMap((item) => item.notes || []), syncedAt: now() };
  }
  if (pathname === "/api/product-orders") {
    return {
      requests: items.filter((item) => item.kind !== "basket"),
      basketItems: items.filter((item) => item.kind === "basket"),
      historyDays: 7,
      syncedAt: now(),
    };
  }
  if (pathname === "/api/mad-rentals") {
    return {
      equipment: items.filter((item) => item.kind === "equipment" || item.action === "equipment"),
      rentals: items.filter((item) => item.kind !== "equipment" && item.action !== "equipment"),
      syncedAt: now(),
    };
  }
  if (pathname === "/api/challenges") {
    const challenges = items.filter((item) => item.kind !== "challenge-update");
    const updates = items.filter((item) => item.kind === "challenge-update");
    const challenge = challenges[0] || null;
    const scoreRows = updates.filter((item) => !challenge || Number(item.challengeId) === Number(challenge.id)).flatMap((item) => item.rows || []);
    const totals = new Map();
    for (const row of scoreRows) {
      const key = `${row.name || row.operator || ""}::${row.pharmacy || ""}`;
      const current = totals.get(key) || { name: row.name || row.operator || "", pharmacy: row.pharmacy || "", quantity: 0, salesAmount: 0 };
      current.quantity += Number(row.quantity || 0);
      current.salesAmount += Number(row.salesAmount || 0);
      totals.set(key, current);
    }
    const individuals = [...totals.values()].sort((a, b) => b.quantity - a.quantity);
    const pharmacyRanking = [...new Set(individuals.map((row) => row.pharmacy))].map((pharmacy) => ({
      pharmacy,
      quantity: individuals.filter((row) => row.pharmacy === pharmacy).reduce((sum, row) => sum + row.quantity, 0),
      salesAmount: individuals.filter((row) => row.pharmacy === pharmacy).reduce((sum, row) => sum + row.salesAmount, 0),
    })).sort((a, b) => b.quantity - a.quantity);
    return {
      items,
      challenge,
      challenges,
      updates,
      individuals,
      pharmacyRanking,
      participants: individuals,
      sales: scoreRows,
      rankings: pharmacyRanking,
      activeChallenges: challenges.filter((item) => item.status === "active" || item.active !== false),
      visible: challenges.length > 0,
      syncedAt: now(),
    };
  }
  if (pathname === "/api/product-catalog") {
    return { items, products: items, totalItems: items.length, totalPages: items.length ? 1 : 0, page: 1, perPage: items.length, syncedAt: now() };
  }
  if (pathname === "/api/nifty-pricing") {
    return { items, decisions: items, summary: {}, syncedAt: now(), updatedAt: now() };
  }
  if (pathname === "/api/windows-companion") {
    return { items, workstations: items, alerts: [], settings: {}, syncedAt: now() };
  }
  return { [spec.listKey]: items, syncedAt: now() };
}

function filterCatalog(items, searchParams) {
  const flattened = items.flatMap((item) => item.kind === "catalog-batch" && Array.isArray(item.rows) ? item.rows : [item]);
  const ean = String(searchParams.get("ean") || "").replace(/\D/g, "");
  const search = String(searchParams.get("search") || "").toLocaleLowerCase("fr").replace(/^\*+/, "");
  const supplier = String(searchParams.get("supplier") || "").toLocaleLowerCase("fr");
  const pharmacy = String(searchParams.get("pharmacy") || "");
  const productTypes = String(searchParams.get("productTypes") || "").toLocaleLowerCase("fr").split(",").filter(Boolean);
  const limit = Math.max(1, Math.min(5000, Number(searchParams.get("limit") || 50)));
  const filtered = flattened.filter((item) => {
    if (ean && String(item.ean || "").replace(/\D/g, "") !== ean) return false;
    if (search && !`${item.product || ""} ${item.ean || ""}`.toLocaleLowerCase("fr").includes(search)) return false;
    if (supplier && !String(item.supplier || "").toLocaleLowerCase("fr").includes(supplier)) return false;
    if (pharmacy && String(item.pharmacy || "") !== pharmacy) return false;
    if (productTypes.length && !productTypes.some((type) => String(item.product_type || item.productType || "").toLocaleLowerCase("fr").includes(type))) return false;
    return true;
  }).slice(0, limit);
  const supplierMap = new Map();
  for (const item of flattened) {
    const name = String(item.supplier || "").trim();
    if (!name) continue;
    supplierMap.set(name, (supplierMap.get(name) || 0) + 1);
  }
  const statsMap = new Map();
  const uniqueProducts = new Set();
  for (const item of flattened) {
    const rawPharmacy = String(item.pharmacy || "");
    const pharmacy = CATALOG_PHARMACIES.find((name) => name.toLocaleLowerCase("fr") === rawPharmacy.toLocaleLowerCase("fr")) || rawPharmacy;
    const ean = String(item.ean || "").replace(/\D/g, "");
    if (!CATALOG_PHARMACIES.includes(pharmacy) || !ean) continue;
    const identity = `${pharmacy}|${ean}`;
    if (uniqueProducts.has(identity)) continue;
    uniqueProducts.add(identity);
    const current = statsMap.get(pharmacy) || { pharmacy, product_count: 0, last_import: 0 };
    current.product_count += 1;
    current.last_import = Math.max(current.last_import, Number(item.imported_at || item.importedAt || item.updatedAt || 0));
    statsMap.set(pharmacy, current);
  }
  return {
    items: filtered,
    products: filtered,
    suppliers: [...supplierMap.entries()].map(([name, count]) => ({ supplier: name, count })),
    stats: [...statsMap.values()],
    totalItems: filtered.length,
    totalPages: filtered.length ? 1 : 0,
    page: 1,
    perPage: limit,
    syncedAt: now(),
  };
}

async function importCatalog(spec, body) {
  if (!Array.isArray(body.rows) || !body.rows.length) return json({ error: "Aucune ligne catalogue valide." }, 400);
  const pharmacy = String(body.pharmacy || "");
  if (body.replacePharmacy && pharmacy) {
    const existing = await listRoute("/api/product-catalog", spec);
    for (const item of existing.filter((entry) => entry.kind === "catalog-batch" && entry.pharmacy === pharmacy)) {
      await deleteRecord("/api/product-catalog", spec, item.id);
    }
  }
  const rows = body.rows.slice(0, 50000).map((row) => ({
    ...row,
    pharmacy: String(row.pharmacy || pharmacy),
    ean: String(row.ean || "").replace(/\D/g, ""),
    product: String(row.product || row.name || row.designation || row.ean || ""),
    product_type: row.product_type || row.productType || "Autre",
    purchase_price: Number(row.purchase_price ?? row.purchasePrice ?? 0),
    sale_price: Number(row.sale_price ?? row.salePrice ?? 0),
  })).filter((row) => row.ean || row.product);
  const created = await createRecord("/api/product-catalog", spec, {
    kind: "catalog-batch", pharmacy, source: body.source || "LGPI", rows,
  });
  return json({ ok: true, id: created.id, imported: rows.length, total: rows.length, pharmacy }, 201);
}

function windowsResponse(items, searchParams) {
  const mode = searchParams.get("mode") || "";
  const settings = items.find((item) => item.kind === "alert-settings") || {
    endSaleEnabled: true,
    title: "Un conseil pour ce patient ✨",
    message: "Une information utile est disponible pour accompagner la délivrance.",
    buttonLabel: "✓ Prise en compte",
    theme: "sunny",
    showConfetti: true,
    stayVisible: true,
  };
  if (mode === "alert-settings") return settings;
  if (mode === "end-sale-popups") return { items: items.filter((item) => item.kind === "end-sale-popup") };
  if (mode === "status") {
    const workstations = items.filter((item) => item.kind === "workstation" || item.workstationId || item.workstation_id);
    return { currentVersion: "1.9.2", workstations, alerts: [], settings, endSalePopups: items.filter((item) => item.kind === "end-sale-popup"), syncedAt: now() };
  }
  return { currentVersion: "1.9.2", settings, endSalePopups: items.filter((item) => item.kind === "end-sale-popup"), niftyEans: [], alerts: [], syncedAt: now() };
}

async function handleBriStore(searchParams, options) {
  const pathname = "/api/bri-store";
  const spec = ROUTES[pathname];
  const method = String(options?.method || "GET").toUpperCase();
  const collection = String(searchParams.get("collection") || "");
  const documentId = String(searchParams.get("id") || "");
  const items = await listRoute(pathname, spec);
  const matching = items.filter((item) => (!collection || item.briCollection === collection) && (!documentId || item.documentId === documentId));
  if (method === "GET") {
    if (documentId) return json({ item: matching[0]?.payload || null });
    const orderField = String(searchParams.get("orderField") || "");
    const orderDirection = searchParams.get("orderDirection") === "asc" ? 1 : -1;
    const maximum = Math.max(1, Math.min(2000, Number(searchParams.get("limit") || 500)));
    const ordered = [...matching].sort((left, right) => {
      const a = left.payload?.[orderField] ?? left.updated_at;
      const b = right.payload?.[orderField] ?? right.updated_at;
      return a === b ? 0 : a > b ? orderDirection : -orderDirection;
    }).slice(0, maximum);
    return json({ items: ordered.map((item) => ({ id: item.documentId, data: item.payload || {} })), syncedAt: now() });
  }
  if (!isAuthenticated()) return requireAuthentication();
  if (method === "POST") {
    const body = await requestBody(options);
    const operations = Array.isArray(body.operations) ? body.operations : [body];
    for (const operation of operations) {
      const existing = (await listRoute(pathname, spec)).find((item) => item.briCollection === operation.collection && item.documentId === String(operation.id));
      if (operation.action === "delete") {
        if (existing) await deleteRecord(pathname, spec, existing.id);
        continue;
      }
      const payload = operation.merge && existing ? { ...(existing.payload || {}), ...(operation.data || {}) } : (operation.data || {});
      if (existing) await patchRecord(pathname, spec, { id: existing.id, briCollection: operation.collection, documentId: String(operation.id), payload });
      else await createRecord(pathname, spec, { briCollection: operation.collection, documentId: String(operation.id), payload });
    }
    return json({ ok: true });
  }
  if (method === "DELETE") {
    for (const item of matching) await deleteRecord(pathname, spec, item.id);
    return json({ ok: true });
  }
  return json({ error: "Méthode BRI non prise en charge." }, 405);
}

async function handleLegacyRequest(pathname, searchParams, options) {
  const method = String(options?.method || "GET").toUpperCase();
  if (usesCompanionWorker(pathname, method, searchParams)) {
    return forwardCompanionWorker(pathname, searchParams, options, method);
  }
  if (FUNCTIONAL_ROUTES.has(pathname)) {
    try {
      return await handleFunctionalRoute(pathname, searchParams, options);
    } catch (error) {
      return json({ error: error?.message || "Cette fonction NG est momentanément indisponible." }, Number(error?.status) || 503);
    }
  }
  const spec = ROUTES[pathname];
  if (!spec) {
    return json({ error: "Fonction NG non reconnue." }, 404);
  }
  if (!pocketbaseUrl) return json({ error: "Connexion à la base NG impossible" }, 503);

  if (pathname === "/api/product-catalog" && method === "GET" && searchParams.get("storage") !== "import") {
    const controller = new AbortController();
    const relayAbort = () => controller.abort();
    options?.signal?.addEventListener("abort", relayAbort, { once: true });
    const archiveStock = searchParams.get("archiveStock") === "1";
    const timeout = window.setTimeout(() => controller.abort(), archiveStock ? 25_000 : 10_000);
    try {
      // La restauration des stocks d'un export LGPI sans colonne quantité doit
      // passer par le Worker Pages publié, qui autorise des lots de 5 000.
      // Le relais Hetzner standard reste volontairement borné à 100 résultats
      // pour les recherches interactives du portail et du Companion.
      const catalogOrigin = archiveStock ? window.location.origin : COMPANION_API_ORIGIN;
      const target = `${catalogOrigin}/api/product-catalog${searchParams.toString() ? `?${searchParams}` : ""}`;
      return await nativeFetch(target, { ...options, signal: controller.signal, cache: "no-store" });
    } catch (error) {
      return json({
        error: error?.name === "AbortError"
          ? "La recherche catalogue met trop de temps à répondre. Réessayez."
          : "La recherche catalogue est momentanément indisponible.",
      }, 503);
    } finally {
      window.clearTimeout(timeout);
      options?.signal?.removeEventListener("abort", relayAbort);
    }
  }
  if (pathname === "/api/challenges" && method === "GET") {
    const controller = new AbortController();
    const relayAbort = () => controller.abort();
    options?.signal?.addEventListener("abort", relayAbort, { once: true });
    const timeout = window.setTimeout(() => controller.abort(), 8_000);
    try {
      return await nativeFetch(`${COMPANION_API_ORIGIN}/api/challenges`, { ...options, signal: controller.signal, cache: "no-store" });
    } catch {
      return json({ challenge: null, challenges: [], updates: [], individuals: [], pharmacyRanking: [], activeChallenges: [], visible: false, syncedAt: now() });
    } finally {
      window.clearTimeout(timeout);
      options?.signal?.removeEventListener("abort", relayAbort);
    }
  }
  if (pathname === "/api/missing-products" && ["GET", "POST", "PATCH", "PUT", "DELETE"].includes(method)) {
    const headers = new Headers(options?.headers || {});
    if (pb.authStore.token) headers.set("Authorization", pb.authStore.token);
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 12_000);
    try {
      const target = `${COMPANION_API_ORIGIN}/api/missing-products${searchParams.toString() ? `?${searchParams}` : ""}`;
      const response = await nativeFetch(target, { ...options, method, headers, signal: controller.signal, cache: "no-store" });
      if (response.status === 401 || response.status === 403) window.dispatchEvent(new CustomEvent("ng-auth-required"));
      return response;
    } catch (error) {
      return json({
        error: error?.name === "AbortError"
          ? "La sauvegarde des produits manquants met trop de temps à répondre. Réessayez."
          : "La sauvegarde des produits manquants est momentanément indisponible.",
      }, 503);
    } finally {
      window.clearTimeout(timeout);
    }
  }
  if (pathname === "/api/nifty-pricing" && method === "GET") {
    return forwardCompanionApi(pathname, searchParams, options, method);
  }
  if (method !== "GET" && !isAuthenticated()) return requireAuthentication();
  if (pathname === "/api/nifty-pricing" && method === "POST") {
    return forwardCompanionApi(pathname, searchParams, options, method);
  }
  try {
    if (pathname === "/api/bri-store") return await handleBriStore(searchParams, options);
    if (method === "GET") {
      const requestedLimit = Number(searchParams.get("limit"));
      const maximum = Number.isFinite(requestedLimit) && requestedLimit > 0
        ? Math.min(POCKETBASE_PAGE_SIZE, Math.floor(requestedLimit))
        : Infinity;
      let items = pathname === "/api/generic-shortages"
        ? await listGenericShortagesDirect()
        : await listRoute(pathname, spec, maximum);
      if (pathname === "/api/product-catalog") return json(filterCatalog(items, searchParams));
      if (pathname === "/api/windows-companion") return json(windowsResponse(items, searchParams));
      if (pathname === "/api/merchandising-analyses" && searchParams.get("pharmacy")) {
        return json({ items: items.filter((item) => item.pharmacy === searchParams.get("pharmacy")), syncedAt: now() });
      }
      if (pathname === "/api/counter-alerts" && searchParams.get("mode") === "active") {
        const pharmacy = searchParams.get("pharmacy");
        const channel = searchParams.get("channel");
        items = items.filter((item) => {
          if (item.status && item.status !== "active") return false;
          if (pharmacy && Array.isArray(item.pharmacies) && !item.pharmacies.includes(pharmacy)) return false;
          if (channel === "portal" && item.showPortal === false) return false;
          if (channel === "tv" && item.showTv === false) return false;
          return true;
        });
      }
      return json(legacyListResponse(pathname, spec, items));
    }
    if (method === "POST") {
      const body = await requestBody(options);
      if (pathname === "/api/product-catalog" && Array.isArray(body.rows)) return importCatalog(spec, body);
      if (pathname === "/api/stock-labels" && Array.isArray(body.items)) {
        const created = [];
        for (const item of body.items.slice(0, 500)) {
          created.push(await createRecord(pathname, spec, item));
        }
        return json({ created: created.length, items: created }, 201);
      }
      if (pathname === "/api/nifty-pricing" && Array.isArray(body.items)) {
        const saved = [];
        for (const item of body.items) saved.push(await createRecord(pathname, spec, { ...item, pharmacy: body.pharmacy || item.pharmacy, kind: "decision" }));
        return json({ ok: true, saved: saved.length, updatedAt: now() });
      }
      if (pathname === "/api/windows-companion") {
        if (body.action === "update_alert_settings") {
          const current = (await listRoute(pathname, spec)).find((item) => item.kind === "alert-settings");
          const payload = { ...body, kind: "alert-settings", id: current?.id };
          const saved = current ? await patchRecord(pathname, spec, payload) : await createRecord(pathname, spec, payload);
          return json({ ok: true, ...saved });
        }
        if (body.action === "save_end_sale_popup") {
          const payload = { ...body, kind: "end-sale-popup", id: body.id || undefined };
          return json(body.id ? await patchRecord(pathname, spec, payload) : await createRecord(pathname, spec, payload));
        }
        if (body.action === "refresh_nifty") return json({ ok: true, count: 0, updatedAt: now() });
        if (body.workstationId || body.workstation_id) return json({ ok: true, currentVersion: "1.9.2", nextHeartbeatInMinutes: 15, serverTime: now(), ...(await createRecord(pathname, spec, { ...body, kind: "workstation" })) });
      }
      if (pathname === "/api/associated-sales" && body.action === "match") {
        const digits = String(body.rawCode || "").replace(/\D/g, "");
        const candidates = [...new Set([digits, digits.slice(-13), digits.slice(0, 13)].filter((value) => value.length >= 8))];
        const pharmacy = String(body.pharmacy || "");
        const associations = await listRoute(pathname, spec);
        const alerts = associations.filter((item) => {
          if (item.status && item.status !== "active") return false;
          if (Array.isArray(item.pharmacies) && pharmacy && !item.pharmacies.includes(pharmacy)) return false;
          const eans = Array.isArray(item.triggerEans) ? item.triggerEans : Array.isArray(item.trigger_eans) ? item.trigger_eans : [];
          return eans.some((ean) => candidates.includes(String(ean).replace(/\D/g, "")));
        });
        return json({ scan: { raw: body.rawCode || "", candidates }, alerts, matchedAt: now() });
      }
      if (pathname === "/api/challenges" && (body.action === "create" || body.action === "update")) {
        const saved = body.action === "update" && body.id
          ? await patchRecord(pathname, spec, body)
          : await createRecord(pathname, spec, { ...body, kind: "challenge" });
        return json(legacyListResponse(pathname, spec, [saved]), body.action === "create" ? 201 : 200);
      }
      if ((body.action === "update" || body.action === "edit") && body.id) return json(await patchRecord(pathname, spec, body));
      if (pathname === "/api/challenges" && body.action === "import") {
        const update = await createRecord(pathname, spec, { ...body, kind: "challenge-update", challengeId: body.challengeId, rows: body.rows || [] });
        const challenge = (await listRoute(pathname, spec)).find((item) => Number(item.id) === Number(body.challengeId));
        return json({ ...legacyListResponse(pathname, spec, challenge ? [challenge] : []), update }, 201);
      }
      return json(await createRecord(pathname, spec, body), 201);
    }
    if (method === "PATCH" || method === "PUT") {
      const body = await requestBody(options);
      if (pathname === "/api/product-orders" && body.kind === "basket" && (body.action === "order_group" || body.action === "reopen_group")) {
        const items = await listRoute(pathname, spec);
        const targets = items.filter((item) => item.kind === "basket" && String(item.laboratory || "").toLocaleLowerCase("fr") === String(body.laboratory || "").toLocaleLowerCase("fr"));
        for (const item of targets) await patchRecord(pathname, spec, { id: item.id, action: body.action === "order_group" ? "ordered" : "reopen", addedBy: body.addedBy });
        return json({ ok: true, syncedAt: now() });
      }
      return json(await patchRecord(pathname, spec, body));
    }
    if (method === "DELETE") {
      await deleteRecord(pathname, spec, searchParams.get("id") || searchParams.get("challengeId") || searchParams.get("endSalePopupId") || searchParams.get("updateId"));
      return json({ ok: true });
    }
    return json({ error: "Méthode non prise en charge." }, 405);
  } catch (error) {
    const status = Number(error?.status) || 503;
    const permission = status === 401 || status === 403;
    const detail = pocketBaseErrorDetail(error);
    if (permission && typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("ng-auth-required"));
    }
    return json({
      error: permission
        ? "Écriture NG non autorisée. Vérifiez les règles PocketBase."
        : status === 400
          ? `Enregistrement NG refusé. ${detail}`
          : "Connexion à la base NG impossible",
      detail,
    }, status >= 400 && status < 600 ? status : 503);
  }
}

export function installLegacyApiBridge() {
  if (nativeFetch || typeof window === "undefined") return;
  nativeFetch = window.fetch.bind(window);
  window.fetch = (input, options = {}) => {
    const rawUrl = typeof input === "string" ? input : input?.url;
    if (typeof rawUrl !== "string" || !rawUrl.startsWith("/api/")) {
      return nativeFetch(input, options);
    }
    const url = new URL(rawUrl, window.location.origin);
    const method = String(options?.method || input?.method || "GET").toUpperCase();
    if (method !== "GET") {
      clearReadCaches();
      return handleLegacyRequest(url.pathname, url.searchParams, options);
    }
    const key = `${url.pathname}${url.search}`;
    const successful = successfulGetResponses.get(key);
    if (successful && successful.expiresAt > now()) return Promise.resolve(successful.response.clone());
    if (successful) successfulGetResponses.delete(key);
    const terminal = terminalGetResponses.get(key);
    if (terminal) return Promise.resolve(terminal.clone());
    const pending = pendingGetRequests.get(key);
    if (pending) return pending.then((response) => response.clone());
    const request = Promise.resolve(handleLegacyRequest(url.pathname, url.searchParams, options))
      .then((response) => {
        if (response.status >= 400 && response.status < 500) terminalGetResponses.set(key, response.clone());
        if (response.ok) successfulGetResponses.set(key, {
          expiresAt: now() + API_RESPONSE_CACHE_TTL_MS,
          response: response.clone(),
        });
        return response;
      })
      .finally(() => {
        if (pendingGetRequests.get(key) === request) pendingGetRequests.delete(key);
      });
    pendingGetRequests.set(key, request);
    return request.then((response) => response.clone());
  };
  pb.authStore.onChange(clearReadCaches);
  window.addEventListener("storage", (event) => {
    if (event.key?.startsWith("portail-pharma:")) clearReadCaches();
  });
  window.addEventListener("click", async (event) => {
    const anchor = event.target instanceof Element ? event.target.closest("a") : null;
    const href = anchor?.getAttribute("href") || "";
    if (!href.startsWith("/api/drive?download=")) return;
    event.preventDefault();
    try {
      const response = await window.fetch(href);
      if (!response.ok) throw new Error("Document introuvable.");
      const blobUrl = URL.createObjectURL(await response.blob());
      const download = document.createElement("a");
      download.href = blobUrl;
      const disposition = response.headers.get("Content-Disposition") || "";
      download.download = decodeURIComponent(disposition.match(/filename\*=UTF-8''([^;]+)/)?.[1] || "document");
      if (anchor?.target === "_blank") download.target = "_blank";
      download.click();
      window.setTimeout(() => URL.revokeObjectURL(blobUrl), 3000);
    } catch (error) {
      window.alert(error?.message || "Le document ne peut pas être téléchargé.");
    }
  });
}
